import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { httpGet, httpPost } from '../../helpers/http.js';

export const loadRegistrationInfo = createAsyncThunk(
    'registration/fetch',
    async (_, { getState }) => {
        const { env, auth } = getState();

        const params = new URLSearchParams();
        (env.registrationKeys || []).forEach(k => params.append("keys", k));

        // Without an idString the Learning API returns the numbers of the caller
        const result = await httpGet(`${registrationsUrl(env)}?${params}`, auth.user);

        const orderMap = new Map(
            env.registrationKeys.map((key, index) => [key, index])
        );

        result.registrationInfo.sort((a, b) => {
            const indexA = orderMap.get(a.key) ?? Infinity;
            const indexB = orderMap.get(b.key) ?? Infinity;
            return indexA - indexB;
        });

        return result;
    },
);

export const saveRegistrationInfo = createAsyncThunk(
    'registration/save',
    async (_, { getState }) => {
        const { registration: state, env, auth } = getState();

        return await httpPost(registrationsUrl(env), auth.user, toSave(state));
    },
);

const registrationsUrl = (env) => `${env.settings.learningApi}/api/1.0/registrations/${encodeURIComponent(env.portalIdentifier)}`;

// Only the kinds the form shows, once each: the stored document is one per person across all portals and can hold
// kinds of other portals, which an empty value would delete, and the API refuses unknown kinds and duplicates
const toSave = ({ current, info }) => {
    const shown = new Set(info.map((kind) => kind.key));
    const registrations = new Map(
        current.registrations
            .filter((r) => shown.has(r.registrationInfoKey))
            .map((r) => [r.registrationInfoKey, { registrationInfoKey: r.registrationInfoKey, registration: r.registration ?? '' }]),
    );

    return { idString: current.idString, registrations: [...registrations.values()] };
};

const registrationSlice = createSlice({
    name: 'registration',
    initialState: {
        status: 'idle',
        error: null,
    },
    reducers: {
        updateRegistration: (state, action) => {
            const { key, changes } = action.payload;

            state.current.registrations = state.current.registrations.some((o) => o.registrationInfoKey === key)
                ? state.current.registrations.map((o) => o.registrationInfoKey === key ? {...o, ...changes} : o)
                : [...state.current.registrations, changes];
            state.dirty = true;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(loadRegistrationInfo.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(loadRegistrationInfo.fulfilled, (state, action) => {
                const {user, registrationInfo} = action.payload;

                state.status = 'success';
                state.current = user,
                state.info = registrationInfo,
                state.dirty = false;
            })
            .addCase(loadRegistrationInfo.rejected, (state, action) => {
                state.status = 'failed';
                state.current = null;
                state.info = [];
                state.error = action.error;
            })
            .addCase(saveRegistrationInfo.pending, (state) => {
                state.status = 'saving';
            })
            .addCase(saveRegistrationInfo.fulfilled, (state) => {
                state.status = 'success';
                state.dirty = false;
            })
            .addCase(saveRegistrationInfo.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error;
            })
    },
});

export const { updateRegistration } = registrationSlice.actions;

export default registrationSlice.reducer;
